<script setup lang="ts">
import Dayjs from '#build/dayjs.imports.mjs'

interface Props {
  showDaysLabel?: boolean
  startDate: Dayjs.Dayjs
  endDate: Dayjs.Dayjs
  dates: Dayjs.Dayjs
  selectedMonth?: boolean
}

const props = defineProps<Props>()
const dayjs = useDayjs()
const wrapper = ref<HTMLDivElement>()

const getWeekNumber = (date: Dayjs.Dayjs) => {
  return date.isoWeek()
}

const GetFirstWeekDate = (date: Dayjs.Dayjs) => {
  return date.weekday(0)
}

const GetLastWeekDate = (date: Dayjs.Dayjs) => {
  return date.weekday(7)
}

const datesToRender = computed(() => {
  const numberOfDaysToRender = GetLastWeekDate(lastDayInMonth.value).diff(
    GetFirstWeekDate(firstDayInMonth.value),
    'days'
  )

  const dates = Array<Dayjs.Dayjs>(numberOfDaysToRender)
    .fill(dayjs(0))
    .map((_, i) => GetFirstWeekDate(firstDayInMonth.value).add(i, 'day'))

  return dates
})

const weekNumbers = computed(() => {
  const weekNumbers = datesToRender.value.map((date) => {
    return getWeekNumber(date)
  })

  return [...new Set(weekNumbers)]
})

const firstDayInMonth = computed(() => {
  return props.dates.date(1)
})

const lastDayInMonth = computed(() => {
  return props.dates.date(props.dates.daysInMonth())
})

const selectedMonth = computed(() => {
  return props.dates.month()
})

const selectedYear = computed(() => {
  return props.dates.year()
})

const isLastDayInMonth = (date: Dayjs.Dayjs) => {
  return date.isSame(date.endOf('month'), 'date')
}

const isFirstDayInMonth = (date: Dayjs.Dayjs) => {
  return date.isSame(date.startOf('month'), 'date')
}

const compareDates = (d1: Dayjs.Dayjs, d2: Dayjs.Dayjs) => {
  return d1.isSame(d2, 'date')
}

const dateInRange = (from: Dayjs.Dayjs, to: Dayjs.Dayjs, date: Dayjs.Dayjs) => {
  return date.isBetween(from.subtract(1), to.add(1))
}

const dateBeforeToday = (date: Dayjs.Dayjs) => {
  return date.isBefore(dayjs())
}

const monthNames = dayjs.months()

onMounted(() => {
  if (props.selectedMonth) {
    wrapper.value?.scrollIntoView({ behavior: 'instant' })
  }
})

const emits = defineEmits(['select-date', 'increment-month', 'decrement-month'])
</script>

<template>
  <div ref="wrapper" class="flex flex-col w-max items-center">
    <div class="flex items-center mb-3">
      <div class="w-28 self-center text-center text-brand-gray-500">
        {{ monthNames[selectedMonth] }} {{ selectedYear }}
      </div>
    </div>
    <div class="flex">
      <div class="flex flex-col" :class="{ 'mt-10': showDaysLabel }">
        <div
          v-for="(number, index) in weekNumbers"
          :key="index"
          class="w-12 h-12 text-sm flex items-center justify-center text-brand-gray-400"
        >
          {{ number }}
        </div>
      </div>
      <div class="flex flex-col">
        <CalenderDaysLabel v-if="showDaysLabel" />
        <div class="grid grid-cols-7">
          <div v-for="(date, index) in datesToRender" :key="index">
            <div
              class="h-10 w-10 my-1 cursor-pointer select-none hover:bg-brand-secondary-100"
              :class="{
                hidden: date.month() != selectedMonth,
                'bg-brand-secondary-100': dateInRange(startDate, endDate, date),
                'rounded-l':
                  compareDates(date, startDate) ||
                  compareDates(date, dayjs(date).weekday(0)) ||
                  isFirstDayInMonth(date),
                'rounded-r':
                  compareDates(date, endDate) ||
                  compareDates(date, dayjs(date).weekday(7)) ||
                  isLastDayInMonth(date),
                'opacity-30 !cursor-default': dateBeforeToday(date),
              }"
              @click="!dateBeforeToday(date) && emits('select-date', date)"
            >
              <div
                :class="{
                  'bg-brand-secondary-400 text-white':
                    compareDates(date, startDate) ||
                    compareDates(date, endDate),
                }"
                class="h-full w-full flex items-center justify-center text-brand-secondary-400"
              >
                {{ date.date() }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
